import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { convertColorToThemeHexCode } from 'Styles/colors.util';

import { useApolloClient } from '@apollo/client';
import Card from '../Card/Card';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import * as Styled from './UpsellCard.styled';
import { addToCart } from '../../services/storefront/cart';

/**
 * An UpsellCard spotlights a specific product variant and allows it to be added to cart
 */
const UpsellCard = ({
  title,
  subtitle,
  image,
  price,
  variantId,
  isAvailable,
  onComplete,
  productId
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const client = useApolloClient();

  const handleAddToBag = async () => {
    setIsFetching(true);

    await addToCart(variantId, 1, undefined, undefined, client, productId)
      .then(onComplete)
      .catch(() => {
        setErrorMessage('Unable to contact server. Please try again.');
      })
      .finally(() => setIsFetching(false));
  };

  return (
    <Card>
      <Styled.CardContent>
        <Styled.ImageContainer>
          <ResponsiveImage
            src={image}
            alt={title}
            background={convertColorToThemeHexCode('grey20')}
            crop={{
              height: 3040,
              width: 3040
            }}
            resolution={{
              small: 300
            }}
          />
        </Styled.ImageContainer>
        <Styled.UpsellContent>
          <Styled.Title>{title}</Styled.Title>
          {subtitle && <Styled.Subtitle>{subtitle}</Styled.Subtitle>}
          <Styled.Price>{`$${price}`}</Styled.Price>

          {isAvailable ? (
            <Styled.AddToBagButton
              name="quick-add"
              isLoading={isFetching}
              onClick={handleAddToBag}
              size="medium"
              data-track-label="add to cart"
              data-track-property={variantId}
              data-track-value={1}
            >
              Add To Bag
            </Styled.AddToBagButton>
          ) : (
            <Styled.SoldOutButton isDisabled>Sold Out</Styled.SoldOutButton>
          )}

          {errorMessage && <Styled.Error>{errorMessage}</Styled.Error>}
        </Styled.UpsellContent>
      </Styled.CardContent>
    </Card>
  );
};

UpsellCard.propTypes = {
  /**
   * Product title
   */
  title: PropTypes.string.isRequired,
  /**
   * Product Subtitle, optional
   */
  subtitle: PropTypes.string,
  /**
   * Product Image
   */
  image: PropTypes.string.isRequired,
  /**
   * Price of the product variant
   */
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /**
   * ID of the product variant
   */
  variantId: PropTypes.number.isRequired,
  /**
   * `true` if the product can be added to bag
   */
  isAvailable: PropTypes.bool,
  /**
   * callback function for when add to bag button is clicked
   */
  onComplete: PropTypes.func,
  /**
   * Product ID
   */
  productId: PropTypes.string
};

UpsellCard.defaultProps = {
  isAvailable: true,
  onComplete: () => {},
  subtitle: null
};

export default UpsellCard;
